import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/**
 * Playlists admin — create/edit sequences of existing content.
 */
export default function PlaylistsAdmin() {
  const [playlists, setPlaylists] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [items, setItems] = useState([]); // [{module, item_id}]
  const [pickModule, setPickModule] = useState("dictation");
  const [pickId, setPickId] = useState("");
  const [modItems, setModItems] = useState({ dictation: [], legal: [], pitman: [], typing: [] });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/playlists").then((r) => setPlaylists(r.data));
  useEffect(() => {
    load();
    // Prefetch each module's items once for the picker
    Promise.all([
      api.get("/dictations").then((r) => ["dictation", r.data]),
      api.get("/legal-passages").then((r) => ["legal", r.data]),
      api.get("/pitman-exercises").then((r) => ["pitman", r.data]),
      api.get("/typing-passages").then((r) => ["typing", r.data]),
    ]).then((pairs) => {
      const map = {};
      pairs.forEach(([k, v]) => { map[k] = v; });
      setModItems(map);
    }).catch(() => {});
  }, []);

  const addItem = () => {
    if (!pickId) { toast.error("Pick an item first"); return; }
    if (items.length >= 100) { toast.error("Max 100 items per playlist"); return; }
    if (items.find((it) => it.module === pickModule && it.item_id === pickId)) {
      toast.error("Already in the playlist"); return;
    }
    setItems([...items, { module: pickModule, item_id: pickId }]);
    setPickId("");
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = items.slice();
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setItems(next);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      await api.post("/admin/playlists", {
        name: form.name, description: form.description || undefined,
        items, is_published: true,
      });
      toast.success("Playlist created");
      setForm({ name: "", description: "" });
      setItems([]);
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;
    await api.delete(`/admin/playlists/${id}`);
    load();
  };

  const togglePublish = async (p) => {
    await api.patch(`/admin/playlists/${p.id}`, { is_published: !p.is_published });
    load();
  };

  const titleOf = (m, id) => {
    const found = (modItems[m] || []).find((x) => x.id === id);
    return found?.title || "(unknown)";
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={create} className="border border-black/20 p-6 space-y-4" data-testid="admin-playlist-form">
        <div className="text-xs uppercase tracking-[0.3em] font-bold">Create playlist</div>
        <Input
          required placeholder="e.g. Beginner Bootcamp (Week 1)"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-none font-mono" data-testid="admin-playlist-name"
        />
        <Textarea
          placeholder="Description (optional)"
          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="rounded-none font-mono" data-testid="admin-playlist-description"
        />

        {/* Item picker */}
        <div className="border border-dashed border-black/30 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Add items in order</div>
          <div className="flex gap-2 flex-wrap">
            <Select value={pickModule} onValueChange={(v) => { setPickModule(v); setPickId(""); }}>
              <SelectTrigger className="rounded-none font-mono w-36" data-testid="admin-playlist-pick-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dictation">Dictation</SelectItem>
                <SelectItem value="legal">Legal</SelectItem>
                <SelectItem value="pitman">Pitman</SelectItem>
                <SelectItem value="typing">Typing</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pickId} onValueChange={setPickId}>
              <SelectTrigger className="rounded-none font-mono flex-1 min-w-[220px]" data-testid="admin-playlist-pick-item"><SelectValue placeholder="Choose an exercise…" /></SelectTrigger>
              <SelectContent>
                {(modItems[pickModule] || []).map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.title || x.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button" onClick={addItem}
              className="rounded-none h-10 px-4 bg-white text-[#002FA7] border-2 border-[#002FA7] hover:bg-[#F4F4F5] text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="admin-playlist-add-item-btn"
            >Add</Button>
          </div>

          {items.length > 0 && (
            <ul className="mt-2 border border-black/10 divide-y divide-black/10" data-testid="admin-playlist-item-list">
              {items.map((it, i) => (
                <li key={`${it.module}-${it.item_id}-${i}`} className="p-2 flex items-center gap-2 text-sm">
                  <span className="w-6 h-6 border border-black/30 flex items-center justify-center text-xs font-black">{i + 1}</span>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold w-16">{it.module}</span>
                  <span className="flex-1 truncate font-mono">{titleOf(it.module, it.item_id)}</span>
                  <button type="button" onClick={() => moveItem(i, -1)} className="text-neutral-500 hover:text-[#002FA7] px-1" disabled={i === 0}>↑</button>
                  <button type="button" onClick={() => moveItem(i, 1)} className="text-neutral-500 hover:text-[#002FA7] px-1" disabled={i === items.length - 1}>↓</button>
                  <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 px-1" data-testid={`admin-playlist-remove-${i}`}>×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button type="submit" disabled={busy} className="rounded-none w-full h-11 bg-[#002FA7] hover:bg-[#0A2540] text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="admin-playlist-submit-btn">
          {busy ? "Creating…" : "Create playlist"}
        </Button>
      </form>

      <div>
        <h3 className="text-lg font-black tracking-tight mb-3">Published playlists</h3>
        <div className="border border-black/15 divide-y divide-black/10" data-testid="admin-playlist-list">
          {playlists.length === 0 && <div className="p-4 font-mono text-sm text-neutral-500">None yet.</div>}
          {playlists.map((p) => (
            <div key={p.id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold truncate">{p.name}</div>
                <div className="text-xs font-mono text-neutral-500">
                  {p.item_count} items · {p.is_published ? "Published" : "Draft"}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  onClick={() => togglePublish(p)} variant="outline" size="sm"
                  className="rounded-none border-black/15" data-testid={`admin-playlist-toggle-${p.id}`}
                >{p.is_published ? "Unpublish" : "Publish"}</Button>
                <Button
                  onClick={() => del(p.id)} variant="outline" size="sm"
                  className="rounded-none border-red-300 text-red-700 hover:bg-red-50"
                  data-testid={`admin-playlist-delete-${p.id}`}
                >Delete</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
